/**
 * Serving now: one tile per tier with the lane and model the router would
 * resolve this minute, and the build stages that run on it. A pinned tier
 * reads amber and carries Release.
 */

import { Button } from "../../../ui/button.js";
import type {
  BuildStage,
  CatalogModel,
  Lane,
  Residency,
  Tier,
  WorkspaceRoutesResponse,
} from "./types.js";
import { formatDate, laneName, ordinal, policyName } from "./format.js";

const BUILD_STAGES: BuildStage[] = ["plan", "implement", "review", "fix"];

interface ServingTilesProps {
  detail: WorkspaceRoutesResponse;
  catalog: Record<Lane, CatalogModel[]>;
  tiers: Tier[];
  /** The workspace policy's lane order. */
  order: Lane[];
  residency: Residency;
  releasing: string | null;
  onRelease: (pinId: string) => void;
}

function modelLabel(catalog: Record<Lane, CatalogModel[]>, lane: Lane, id: string): string {
  return (catalog[lane] ?? []).find((m) => m.id === id)?.label ?? id;
}

export function ServingTiles({
  detail,
  catalog,
  tiers,
  order,
  residency,
  releasing,
  onRelease,
}: ServingTilesProps) {
  return (
    <>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {tiers.map((tier) => {
          const route = detail.routes[tier];
          const pin = route?.source === "pin" ? route.pin : undefined;
          const position = route ? order.indexOf(route.lane) : -1;
          const policyVia = position >= 0 ? `${ordinal(position + 1)} lane` : "rescue lane";
          const stages = BUILD_STAGES.filter((stage) => detail.stages?.[stage] === tier);
          return (
            <div
              key={tier}
              data-testid={`serving-tile-${tier}`}
              className={`rounded-[var(--radius-control)] border px-2.5 py-2 ${
                pin
                  ? "border-[var(--warning)] bg-[var(--warning-muted)]"
                  : "border-[var(--border-subtle)] bg-[var(--print-bg)]"
              }`}
            >
              <div
                className="text-[10px] uppercase tracking-wider"
                style={{ color: "var(--text-label)", fontFamily: "var(--font-mono)" }}
              >
                {tier}
              </div>
              {route ? (
                <>
                  <div className="text-[13px] font-medium" style={{ color: "var(--text-primary)" }}>
                    {modelLabel(catalog, route.lane, route.model)}
                  </div>
                  <div
                    className="truncate text-[10.5px]"
                    title={route.model}
                    style={{ color: "var(--text-tertiary)", fontFamily: "var(--font-mono)" }}
                  >
                    {route.model}
                  </div>
                  {stages.length > 0 && (
                    <div
                      className="mt-1 text-[11px]"
                      data-testid={`serving-tile-${tier}-stages`}
                      style={{ color: "var(--text-secondary)" }}
                    >
                      Build stages: {stages.join(", ")}
                    </div>
                  )}
                  <div className="mt-1 text-[11px]" style={{ color: "var(--text-secondary)" }}>
                    via{" "}
                    {pin
                      ? `${laneName(route.lane)} · pinned ${formatDate(pin.createdAt)}, “${pin.reason}”`
                      : `${laneName(route.lane)} · ${policyName(residency)}, ${policyVia}`}
                  </div>
                  {pin && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="mt-1 h-6 px-2 text-xs"
                      aria-label={`Release ${tier} pin`}
                      loading={releasing === pin.id}
                      disabled={releasing !== null}
                      onClick={() => onRelease(pin.id)}
                    >
                      Release
                    </Button>
                  )}
                </>
              ) : (
                <div className="text-xs" style={{ color: "var(--error)" }}>
                  No lane can serve this tier. Calls fail closed.
                </div>
              )}
            </div>
          );
        })}
      </div>
      {detail.rescue && (
        <div className="mt-1.5 text-xs" style={{ color: "var(--text-tertiary)" }}>
          {detail.rescue}
        </div>
      )}
    </>
  );
}
